import { useMemo } from "react";
import useSWR from "swr";
import useFetcher from "@/utils/use-fetcher";
import { PathPickKream, PathPickPoizon } from "@/features/pathpick/types";

/**
 * 라인시트 상품 1개의 크림·포이즌 시세 — `GET /buyers/{id}/line-sheet-sets/{id}/market-prices`.
 * 시세는 라인시트 상품에 붙는 값이라 오더시트가 아니라 라인시트세트 단위로 받는다.
 * 매칭이 없는 마켓은 키가 없고, 두 마켓 다 없는 상품은 목록에 오지 않는다.
 */
export interface LineSheetProductMarketPrice {
  lineSheetProductId: number;
  // 부대비용 계수 — 현재 공급가에 (1 + uplift) 를 곱한다. 관세 판정은 패스픽과 같고,
  // 발주 수수료 대신 이 바이어의 우노 수수료가 들어 있다(서버가 바이어별로 계산해 준다).
  // 예상 매입가는 서버가 주지 않는다. 바이어가 컨디션을 고치면 공급가가 화면에서 바뀌므로
  // 프론트가 그때그때 계산한다(calcEstimatedSupplyKrw).
  uplift: number;
  // 위 계수의 구성(곱하는 순서) — 계산식을 풀어 보여줄 때만 쓴다. (1 + rate) 를 모두 곱해 1 을
  // 빼면 uplift 다. key: shipping(운송비) / duty(관세) / vat(부가세) /
  // uno_fee(우노 수수료 — 부가세 10% 가 이미 얹힌 값. 설정값 5% 면 5.5% 로 온다)
  upliftFactors?: { key: string; rate: number }[];
  // 옵션명 통일 표기 — 패스픽과 같은 서버 정규화. 사이즈별 시세 모달이 이 이름으로 마켓 옵션과 맞춘다.
  // (구버전 백엔드 응답에는 없을 수 있어 optional)
  options?: { name: string; displayName: string }[];
  kream?: PathPickKream | null;
  poizon?: PathPickPoizon | null;
}

/**
 * 예상 매입가의 세트 단위 계산 재료.
 *   예상 매입가 = (공급가 × (1 + uplift) × rates[통화]) 를 천원 단위로 반올림
 * 환율은 패스픽 목록과 같은 '그날의 기준가'다 — 화면 상단의 실시간 환율과 조금 다를 수 있다.
 * 패스픽 핫딜 식과 달리 정액 수수료(통관수수료·국내배송비·박스비)는 없다.
 */
export interface LineSheetSetMarketPricing {
  // 통화 → 원화 환율. 서버가 환율을 못 받은 통화는 없다(그 통화는 예상 매입가를 비운다).
  rates: Record<string, number>;
}

export interface LineSheetSetMarketPricesResponse {
  // false = 패스픽 멤버십이 없거나 만료 → products 는 비어 있다.
  enabled: boolean;
  pricing?: LineSheetSetMarketPricing;
  products: LineSheetProductMarketPrice[];
}

const EMPTY_PRICING: LineSheetSetMarketPricing = { rates: {} };

export function getLineSheetSetMarketPricesKey(
  buyerId: number | null | undefined,
  lineSheetSetId: number | null | undefined
) {
  if (!buyerId || !lineSheetSetId || lineSheetSetId < 0) {
    return null;
  }
  return {
    url: `/buyers/${buyerId}/line-sheet-sets/${lineSheetSetId}/market-prices`,
    params: {},
  };
}

/**
 * 라인시트세트의 상품별 시세 지도. 패스픽 멤버(호출부가 판단)일 때만 조회한다.
 *
 * 응답은 세트 전체(모든 탭)의 상품이라 탭을 오가도 다시 부르지 않는다. 시세는 매일
 * 수집분이라 화면에 머무는 동안 다시 받을 이유가 없어 포커스 재검증을 끈다.
 */
export default function useLineSheetSetMarketPrices(
  buyerId: number | null | undefined,
  lineSheetSetId: number | null | undefined,
  enabled: boolean
) {
  const fetcher = useFetcher<{}, LineSheetSetMarketPricesResponse>();
  const { data, isLoading, error } = useSWR(
    enabled ? getLineSheetSetMarketPricesKey(buyerId, lineSheetSetId) : null,
    fetcher,
    { revalidateOnFocus: false, revalidateIfStale: false }
  );

  const byProductId = useMemo(() => {
    const map = new Map<number, LineSheetProductMarketPrice>();
    data?.products?.forEach((product) => {
      map.set(product.lineSheetProductId, product);
    });
    return map;
  }, [data]);

  return {
    // 서버가 멤버십 없음이라고 하면(enabled=false) 시세 열을 그리지 않는다.
    isEnabled: enabled && data?.enabled === true,
    isLoading: enabled && isLoading,
    error,
    byProductId,
    pricing: data?.pricing ?? EMPTY_PRICING,
  };
}
